import type { UI_PreviewCardImageSizeOptions, UI_PreviewCardProps } from 'ui/components/2-molecules/Card/PreviewCard/PreviewCard';
import styles from './PreviewCardList.module.scss';
import { UI_PreviewCard } from 'ui/components/2-molecules/Card/PreviewCard/PreviewCard';
import { ActiveVideoProvider, DEFAULT_MAX_VIDEOS } from 'ui/components/2-molecules/Card/PreviewCard/ActiveVideoContext';
import { UI_Button } from 'ui/components/1-atoms/Action/Button/Button';
import { UI_TopLevelGrid } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGrid';
import { UI_TopLevelGridCell, UI_TopLevelGridColOptions } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGridCell';
import { useState } from 'react';
import classNames from 'classnames';
import { UI_GridCellProps } from 'ui/components/4-habitats/Grid/GridCell';

export interface UI_PreviewCardListProps {
	items: UI_PreviewCardProps[];
	buttonText?: string;
	desktopWidth?: UI_GridCellProps['desktopWidth'];
	mobileWidth?: UI_GridCellProps['mobileWidth'];
	marginBottom?: boolean;
}

const indexToWidthAndOffsetMap: Record<
	number,
	{ width: UI_TopLevelGridColOptions; offset: UI_TopLevelGridColOptions | null; imgSize: UI_PreviewCardImageSizeOptions }
> = {
	0: {
		width: '10col',
		offset: null,
		imgSize: 'medium',
	},
	1: {
		width: '6col',
		offset: '1col',
		imgSize: 'large',
	},
	2: {
		width: '6col',
		offset: '1col',
		imgSize: 'small',
	},
	3: {
		width: '6col',
		offset: null,
		imgSize: 'large',
	},
	4: {
		width: '10col',
		offset: '1col',
		imgSize: 'medium',
	},
	5: {
		width: '6col',
		offset: '1col',
		imgSize: 'small',
	},
	6: {
		width: '10col',
		offset: null,
		imgSize: 'medium',
	},
	7: {
		width: '6col',
		offset: '1col',
		imgSize: 'small',
	},
	8: {
		width: '6col',
		offset: '1col',
		imgSize: 'large',
	},
	9: {
		width: '6col',
		offset: null,
		imgSize: 'large',
	},
	10: {
		width: '6col',
		offset: '1col',
		imgSize: 'small',
	},
	11: {
		width: '10col',
		offset: '1col',
		imgSize: 'medium',
	},
};

export const UI_PreviewCardList: React.FC<UI_PreviewCardListProps> = ({ items, buttonText, marginBottom }) => {
	const defaultElementsNumber = 12;
	const [shownNumber, setShownNumber] = useState<number>(items.length > defaultElementsNumber ? defaultElementsNumber : items.length);

	return (
		<div className={classNames(styles.PreviewCardList)}>
			<ActiveVideoProvider>
				<UI_TopLevelGrid wrap scaleDown nogutter>
					{items.slice(0, shownNumber).map((item, index) => {
						const { width, offset, imgSize } = indexToWidthAndOffsetMap[index % 12];
						return (
							<UI_TopLevelGridCell key={index} desktopWidth={width} desktopOffset={offset} mobileWidth="100">
								<UI_PreviewCard {...item} id={index} imageSize={imgSize} eagerPlay={index < DEFAULT_MAX_VIDEOS} />
							</UI_TopLevelGridCell>
						);
					})}

					{shownNumber < items.length && buttonText && (
						<UI_TopLevelGridCell desktopWidth="24col">
							<UI_Button
								inheritsColor
								onClick={() => {
									if (shownNumber <= items.length - defaultElementsNumber) {
										setShownNumber(shownNumber + defaultElementsNumber);
									} else {
										const difference = items.length - shownNumber;
										setShownNumber(shownNumber + difference);
									}
								}}
								style="secondary"
								title={buttonText}
								type="button"
								className={classNames(styles.PreviewCardList_btn, { [styles.PreviewCardList_btn___marginBottom]: marginBottom })}
							>
								{buttonText}
							</UI_Button>
						</UI_TopLevelGridCell>
					)}
				</UI_TopLevelGrid>
			</ActiveVideoProvider>
		</div>
	);
};
